/**
 * Flow tape API — server-driven queries against `/api/options-flow/flow`.
 *
 * The tape is ~206M rows in QuestDB and is returned newest-first, so a single symbol can fill
 * any page you fetch: 5,000 unfiltered rows came back as 5,000 NVDA prints inside one second.
 * Filtering or sorting a fetched window in the browser therefore cannot work — every filter has
 * to be a query parameter that QuestDB applies to the whole match set. That is the one rule this
 * module exists to enforce; nothing here filters rows client-side.
 *
 * Counts and paging come from the server too: `total` is a real `count()` over the same WHERE
 * clause, flagged by `total_exact`, so page counts are honest rather than derived from a window.
 */

import { getApiUrl } from '../config/api';
import { getAuthToken } from './authUtils';

export const FLOW_TAPE_ENDPOINT = '/api/options-flow/flow';
export const FLOW_AGGREGATE_ENDPOINT = '/api/options-flow/aggregate';

/** Columns the backend will sort by (whitelisted server-side; anything else falls back to time). */
export const FLOW_SORT_FIELDS = [
  { key: 'timestamp', label: 'Time' },
  { key: 'premium', label: 'Premium' },
  { key: 'size', label: 'Print size' },
  { key: 'volume', label: 'Volume' },
  { key: 'strike', label: 'Strike' },
  { key: 'dte', label: 'DTE' },
  { key: 'ticker', label: 'Ticker' },
  { key: 'expiry', label: 'Expiry' },
] as const;

/** Dimensions the aggregate endpoint can group by. */
export const FLOW_GROUP_FIELDS = [
  { key: 'ticker', label: 'Ticker' },
  { key: 'option_type', label: 'Call / Put' },
  { key: 'side', label: 'Tape side' },
  { key: 'expiry', label: 'Expiry' },
  { key: 'dte', label: 'DTE' },
  { key: 'strike', label: 'Strike' },
  { key: 'exchange', label: 'Exchange' },
  { key: 'condition', label: 'Condition' },
] as const;

export interface FlowTapeQuery {
  ticker?: string;
  search?: string;
  side?: string;
  optionType?: string;
  minPremium?: string;
  maxPremium?: string;
  minSize?: string;
  minStrike?: string;
  maxStrike?: string;
  minDte?: string;
  maxDte?: string;
  expiry?: string;
  moneyness?: string;
  /** 'stock' | 'etf' | 'index' — applied in SQL, not over a fetched page. */
  underlyingClass?: string;
  /** 'large' | 'medium' | 'small' — resolved server-side from cached market caps. */
  capTier?: string;
  fromTs?: string;
  toTs?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface FlowTapeRow {
  id: string;
  ticker: string;
  optionType: string;
  strike: number | null;
  expiry: string;
  dte: number | null;
  premium: number | null;
  size: number | null;
  volume: number | null;
  price: number | null;
  side: string;
  condition: string;
  exchange: string;
  time: string;
  /** Full source record, for the detail row and any column the table chooses to show. */
  raw: Record<string, unknown>;
}

export interface FlowTapePage {
  rows: FlowTapeRow[];
  /** True match count from QuestDB when `totalExact`; otherwise the size of a fetched window. */
  total: number;
  totalExact: boolean;
  offset: number;
  pageSize: number;
  /** Rows in the whole store, for context — not the match count. */
  persistedTotal: number;
  sortBy: string;
  sortDir: string;
}

export interface FlowAggregateBucket {
  bucket: string;
  prints: number;
  premium: number;
  contracts: number;
}

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string {
  return v == null ? '' : String(v);
}

/** Trim + drop empties so we never send `min_premium=` and have it parsed as a filter. */
function put(qs: URLSearchParams, key: string, value: string | number | undefined | null): void {
  if (value == null) return;
  const s = String(value).trim();
  if (!s) return;
  qs.set(key, s);
}

export function buildFlowTapeQuery(q: FlowTapeQuery): URLSearchParams {
  const qs = new URLSearchParams();
  qs.set('data_source', 'questdb');
  put(qs, 'ticker', q.ticker?.toUpperCase());
  // `search` is a LIKE on ticker; the backend ignores it when an exact ticker is set.
  if (!q.ticker) put(qs, 'search', q.search?.toUpperCase());
  put(qs, 'side', q.side);
  put(qs, 'option_type', q.optionType);
  put(qs, 'min_premium', q.minPremium);
  put(qs, 'max_premium', q.maxPremium);
  put(qs, 'min_size', q.minSize);
  put(qs, 'min_strike', q.minStrike);
  put(qs, 'max_strike', q.maxStrike);
  put(qs, 'min_dte', q.minDte);
  put(qs, 'max_dte', q.maxDte);
  put(qs, 'expiry', q.expiry);
  put(qs, 'moneyness', q.moneyness);
  put(qs, 'underlying_class', q.underlyingClass);
  put(qs, 'underlying_cap', q.capTier);
  put(qs, 'from_ts', q.fromTs);
  put(qs, 'to_ts', q.toTs);
  put(qs, 'sort_by', q.sortBy);
  put(qs, 'sort_dir', q.sortDir);
  return qs;
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/**
 * One print → one row. `sequence` + `timestamp` identify a print; the index disambiguates the
 * rare duplicate so React keys stay unique without inventing an id.
 */
export function normalizeFlowTapeRow(raw: Record<string, unknown>, index: number): FlowTapeRow {
  const ticker = str(raw.Ticker || raw.Underlying || raw.ticker).toUpperCase();
  const expiryRaw = str(raw['Expiry Date'] || raw.expiry);
  const expiry = /^\d{8}$/.test(expiryRaw)
    ? `${expiryRaw.slice(0, 4)}-${expiryRaw.slice(4, 6)}-${expiryRaw.slice(6, 8)}`
    : expiryRaw.slice(0, 10);
  return {
    id: `${str(raw.sequence || raw.trade_sequence)}|${str(raw.timestamp || raw.ts)}|${index}`,
    ticker,
    optionType: str(raw['Option Type'] || raw.option_type).toUpperCase().slice(0, 1),
    strike: num(raw.Strike ?? raw.strike),
    expiry,
    dte: num(raw.DTE ?? raw.dte),
    premium: num(raw.Premium ?? raw.premium),
    size: num(raw.Volume ?? raw.size),
    volume: num(raw.Volume ?? raw.volume),
    price: num(raw.Last ?? raw.price),
    side: str(raw.Side || raw.Bid_Ask || raw.bid_ask),
    condition: str(raw.Condition || raw.condition),
    exchange: str(raw.exchange ?? raw.trade_exchange),
    time: str(raw.timestamp || raw.ts || raw.Time),
    raw,
  };
}

export async function fetchFlowTape(q: FlowTapeQuery, signal?: AbortSignal): Promise<FlowTapePage> {
  const qs = buildFlowTapeQuery(q);
  qs.set('limit', String(Math.max(1, Math.min(q.limit ?? 100, 1000))));
  qs.set('offset', String(Math.max(0, q.offset ?? 0)));
  const res = await fetch(getApiUrl(`${FLOW_TAPE_ENDPOINT}?${qs.toString()}`), {
    headers: authHeaders(),
    cache: 'no-store',
    signal,
  });
  if (!res.ok) throw new Error(`Flow tape request failed (${res.status})`);
  const json = await res.json();
  if (!json?.success || !Array.isArray(json.data)) {
    throw new Error(typeof json?.message === 'string' ? json.message : 'Flow tape returned no data');
  }
  return {
    rows: (json.data as Record<string, unknown>[]).map(normalizeFlowTapeRow),
    total: Number(json.total) || 0,
    // Older builds of the API do not send the flag; treat its absence as "not exact".
    totalExact: json.total_exact === true,
    offset: Number(json.offset) || 0,
    pageSize: Number(json.page_size) || 0,
    persistedTotal: Number(json.persisted_total_rows) || 0,
    sortBy: str(json.sort_by) || 'timestamp',
    sortDir: str(json.sort_dir) || 'desc',
  };
}

export async function fetchFlowAggregate(
  q: FlowTapeQuery,
  groupBy: string,
  limit = 25,
  signal?: AbortSignal,
): Promise<FlowAggregateBucket[]> {
  const qs = buildFlowTapeQuery(q);
  qs.set('group_by', groupBy);
  qs.set('limit', String(Math.max(1, Math.min(limit, 500))));
  qs.delete('sort_by');
  qs.delete('sort_dir');
  const res = await fetch(getApiUrl(`${FLOW_AGGREGATE_ENDPOINT}?${qs.toString()}`), {
    headers: authHeaders(),
    cache: 'no-store',
    signal,
  });
  if (!res.ok) throw new Error(`Flow aggregate request failed (${res.status})`);
  const json = await res.json();
  if (!json?.success || !Array.isArray(json.buckets)) return [];
  return (json.buckets as Record<string, unknown>[]).map((b) => ({
    bucket: str(b.bucket) || '—',
    prints: Number(b.prints) || 0,
    premium: Number(b.premium) || 0,
    contracts: Number(b.contracts) || 0,
  }));
}

/**
 * Filters the tape cannot honour today, with the reason. `flow_buffer` stores 0 for these on
 * every row (verified over 29.4M rows in a 24h window), so offering the control would return an
 * empty board and read as a broken filter. Restore them once ingest populates the columns.
 */
export const FLOW_UNAVAILABLE_FILTERS: { label: string; reason: string }[] = [
  { label: 'Open interest', reason: 'tape rows carry no open interest (oi is 0)' },
];

/**
 * Moneyness is computed server-side from the strike against the Polygon underlying price now
 * stamped onto each print. Index underlyings (SPX, SPXW, NDX, VIX) are absent from Polygon's
 * stocks snapshot, so their prints stay unpriced and fall outside every moneyness bucket rather
 * than being approximated from an ETF proxy.
 */
export const FLOW_MONEYNESS_OPTIONS = ['ITM', 'ATM', 'OTM'] as const;

/** `2026-08-15T18:48:33` → `18:48:33`, keeping the date when it is not today's session. */
export function formatTapeTime(ts: string): string {
  if (!ts) return '—';
  const t = ts.includes('T') ? ts.split('T')[1] : ts;
  return (t || ts).slice(0, 8);
}

export function formatPremium(v: number | null): string {
  if (v == null) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(v >= 1e7 ? 0 : 1)}M`;
  if (v >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${Math.round(v)}`;
}
